import React, { useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { FaEye, FaDownload, FaArrowRight } from "react-icons/fa";

const About = () => {
  return (
    <section id="about" className="about">
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: false, amount: 0.5 }}
      >
        À propos
      </motion.h2>

      <motion.div
        className="about-card"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.1 }}
      >
        <div className="about-container">
          {/* Photo Section */}
          <div className="about-left-section">
            <motion.div
              className="about-image-wrapper"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <div className="image-border"></div>
              <img
                src="/assets/img/portrait.png"
                alt="Elisa Gomez"
                className="about-image"
              />
            </motion.div>

            {/* Key Focus Badges sous la photo */}
            <motion.div
              className="key-focus"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.5, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <h3>Centres d'intérêt</h3>
              <div className="badges">
                <span className="badge">Cadrage IA</span>
                <span className="badge">Cas d'usage métier</span>
                <span className="badge">LLM & automatisation</span>
                <span className="badge">Interface métier-tech</span>
              </div>
            </motion.div>
          </div>

          {/* Text Content */}
          <div className="about-content">
            <div className="about-text">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.3 }}
              >
                Cheffe de projet Intelligence Artificielle, j’interviens à l’interface entre enjeux métiers et solutions techniques pour structurer et déployer des usages concrets de l’IA.
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.3 }}
              >
                J’accompagne les organisations dans l’identification de cas d’usage pertinents, la structuration des besoins et des données, ainsi que la mise en place de solutions adaptées (LLM, RAG, automatisation, orchestration).
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.3 }}
              >
                Mon approche repose sur une forte capacité à clarifier des problématiques complexes, à relier vision métier et contraintes techniques, et à construire des solutions simples, cohérentes et réellement utiles sur le terrain.
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.3 }}
              >
                Issue d’un parcours d’ingénierie et forte de 18 ans d’expérience en gestion de projets dans le secteur du bâtiment, j’ai développé une vision globale des systèmes et une exigence forte sur la qualité, la clarté et la robustesse des solutions.
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.5, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.3 }}
              >
                Aujourd’hui en alternance en tant que chef de projet IA, je poursuis cette évolution en travaillant sur des problématiques concrètes d’intégration de l’intelligence artificielle en entreprise.
              </motion.p>
            </div>
          </div>
        </div>

        {/* Timeline horizontale - DANS la carte */}
        <motion.div
          className="timeline-section"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.7 }}
          viewport={{ once: true, amount: 0.3 }}
        >
          <h3 className="timeline-title">Mon Parcours</h3>

          <div className="timeline-container">
            {/* Ligne horizontale animée */}
            <motion.div
              className="timeline-line"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              transition={{ duration: 1.5, ease: "easeInOut", delay: 0.9 }}
              viewport={{ once: true, amount: 0.3 }}
            />

            {/* Points et étapes */}
            <div className="timeline-milestones">
              {/* Point 2001 */}
              <motion.div
                className="timeline-milestone"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.1, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <div className="milestone-point">
                  <div className="milestone-dot"></div>
                </div>
                <span className="milestone-year">
                  <span className="year-single">2001</span>
                  <span className="year-range">2001 - 2006</span>
                </span>
              </motion.div>

              {/* Segment 2001-2006 */}
              <motion.div
                className="timeline-segment"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.2, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <h4 className="segment-title">INSA de Lyon</h4>
                <p className="segment-description">Ingénieur Génie Civil et Urbanisme</p>
              </motion.div>

              {/* Point 2006 */}
              <motion.div
                className="timeline-milestone"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.3, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <div className="milestone-point">
                  <div className="milestone-dot"></div>
                </div>
                <span className="milestone-year">
                  <span className="year-single">2006</span>
                  <span className="year-range">2006 - 2012</span>
                </span>
              </motion.div>

              {/* Segment 2006-2012 */}
              <motion.div
                className="timeline-segment"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.4, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <h4 className="segment-title">ARP & VOXOA</h4>
                <p className="segment-description">Programmiste</p>
              </motion.div>

              {/* Point 2012 */}
              <motion.div
                className="timeline-milestone"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.5, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <div className="milestone-point">
                  <div className="milestone-dot"></div>
                </div>
                <span className="milestone-year">
                  <span className="year-single">2012</span>
                  <span className="year-range">2012 - 2018</span>
                </span>
              </motion.div>

              {/* Segment 2012-2018 */}
              <motion.div
                className="timeline-segment"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.6, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <h4 className="segment-title">SAMOP & Ascoréal</h4>
                <p className="segment-description">Programmiste et cheffe de projet</p>
              </motion.div>

              {/* Point 2018 */}
              <motion.div
                className="timeline-milestone"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.7, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <div className="milestone-point">
                  <div className="milestone-dot"></div>
                </div>
                <span className="milestone-year">
                  <span className="year-single">2018</span>
                  <span className="year-range">2018 - 2025</span>
                </span>
              </motion.div>

              {/* Segment 2018-2025 */}
              <motion.div
                className="timeline-segment"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.8, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <h4 className="segment-title">Groupe SERL</h4>
                <p className="segment-description">Cheffe de projet en pilotage et conduite d'opération</p>
              </motion.div>

              {/* Point 2025 */}
              <motion.div
                className="timeline-milestone"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.9, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <div className="milestone-point">
                  <div className="milestone-dot"></div>
                </div>
                <span className="milestone-year">
                  <span className="year-single">2025</span>
                  <span className="year-range">2025</span>
                </span>
              </motion.div>

              {/* Segment après 2025 */}
              <motion.div
                className="timeline-segment"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 2.0, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <h4 className="segment-title">Développement Web & Mobile Full-Stack</h4>
                <p className="segment-description">La Capsule (RNCP&nbsp;6)</p>
              </motion.div>

              {/* Point 2026 */}
              <motion.div
                className="timeline-milestone"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 2.1, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <div className="milestone-point">
                  <div className="milestone-dot"></div>
                </div>
                <span className="milestone-year">
                  <span className="year-single">2026</span>
                  <span className="year-range">2026</span>
                </span>
              </motion.div>

              {/* Segment après 2026 */}
              <motion.div
                className="timeline-segment"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 2.2, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <h4 className="segment-title">Chef de projet IA</h4>
                <p className="segment-description">The Coding Machine - Formation Le Bahut</p>
              </motion.div>
            </div>

            {/* Flèche animée en fin de parcours */}
            <motion.div
              className="timeline-arrow"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 1.9, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <FaArrowRight />
            </motion.div>
          </div>
        </motion.div>

        {/* CV Buttons - DANS la carte, après la timeline */}
        <motion.div
          className="about-links"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.0, duration: 0.5, ease: "easeOut" }}
          viewport={{ once: true, amount: 0.3 }}
        >
          <MagneticButton
            href="/assets/CV_Elisa_Gomez_web.pdf"
            target="_blank"
            rel="noopener noreferrer"
            primary
          >
            <FaEye />
            Voir mon CV
          </MagneticButton>
          <MagneticButton
            href="/assets/CV_Elisa_Gomez_web.pdf"
            download="CV_Elisa_Gomez_web.pdf"
          >
            <FaDownload />
            Télécharger
          </MagneticButton>
        </motion.div>
      </motion.div>
    </section>
  );
};

// Composant Bouton Magnétique (réutilisé de Home)
const MagneticButton = ({ children, href, primary, ...props }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const springConfig = { damping: 15, stiffness: 150 };
  const springX = useSpring(x, springConfig);
  const springY = useSpring(y, springConfig);

  const handleMouseMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    x.set((e.clientX - centerX) * 0.3);
    y.set((e.clientY - centerY) * 0.3);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.a
      ref={ref}
      href={href}
      className={`cv-btn ${primary ? "cv-btn-primary" : "cv-btn-secondary"}`}
      style={{ x: springX, y: springY }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      {...props}
    >
      {children}
    </motion.a>
  );
};

export default About;
